import { Alert, Button, Typography } from '@execonline-inc/execonline-ui';
import clsx from 'clsx';
import { Maybe } from 'maybeasy';
import * as React from 'react';
import { T } from '../../../../../Translations';
import { StartingLater } from '../LicenseAlerts/Selectors';
import { foldInviteTarget, inviteTarget } from '../InviteTarget';
import { shareStore } from '../Stores/ShareStore';

// People whose licenses only start later (DVLP-8068), named when there is one.
export interface LaterStarts {
  count: number;
  sole: Maybe<StartingLater>;
}

// License dates carry no time of day and parse to UTC midnight.
const formatStartDate = (date: Date): string =>
  date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', timeZone: 'UTC' });

export const startsLaterHeadline = (later: LaterStarts): React.ReactNode =>
  later.sole.cata<React.ReactNode>({
    Just: ({ firstName, startsOn }) => (
      <T
        kind="{{name}}'s license starts {{date}}."
        name={firstName}
        date={formatStartDate(startsOn)}
      />
    ),
    Nothing: () => <T kind="{{count}} participants' licenses start later." count={later.count} />,
  });

const startsLaterBody = (count: number): React.ReactNode =>
  inviteTarget(shareStore.state)
    .map((target) =>
      foldInviteTarget(
        target,
        () =>
          count === 1 ? (
            <T kind="They can access this collection starting that day." />
          ) : (
            <T kind="Each can access this collection when their license starts." />
          ),
        () =>
          count === 1 ? (
            <T kind="They can access this course starting that day." />
          ) : (
            <T kind="Each can access this course when their license starts." />
          ),
      ),
    )
    .getOrElse(() => <></>);

// Nobody is missing a course, but some people's licenses only start later.
// A render function rather than a component, so the banner swaps in place for
// the license alert banner without remounting.
export const startsLaterBanner = (
  later: LaterStarts,
  reloading: boolean,
  onViewDetails: () => void,
): React.ReactNode => (
  <Alert
    key="banner"
    status="warning"
    variant="outlined"
    icon
    radius="md"
    className="mb-6"
    aria-busy={reloading}
    data-intercom-target="share-license-start-banner"
  >
    <div
      className={clsx(
        'flex w-full flex-wrap items-center gap-x-4 gap-y-2 transition-opacity',
        reloading && 'opacity-60',
      )}
    >
      <div className="min-w-0 flex-1">
        <Typography variant="bodyMedium" fontWeight="medium">
          {startsLaterHeadline(later)}
        </Typography>
        <Typography variant="bodySmall" className="text-muted-foreground">
          {startsLaterBody(later.sole.isJust() ? 1 : later.count)}
        </Typography>
      </div>
      <Button
        variant="outlined"
        className="bg-card"
        onClick={onViewDetails}
        data-intercom-target="share-license-start-view-details"
      >
        <T kind="View details" />
      </Button>
    </div>
  </Alert>
);
